import type { DiscountApprovalSummary, DiscountApprovalView, OutletSummary } from "@kopi/contracts";
import { DISCOUNT_APPROVAL_STATUSES, type DiscountApprovalStatus } from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Textarea } from "@kopi/ui/components/textarea";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { EyeIcon, ScaleIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { discountValueText, percentText } from "@/discounts/labels";
import { discountApprovalQuery, discountApprovalsKey, discountApprovalsQuery } from "@/discounts/queries";
import { discounts } from "@/i18n/messages/discounts";
import { type AreaTranslate, useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { meScopesQuery, outletsQuery } from "@/lib/queries";

/** Radix Select items can't have an empty value, so "all statuses" has its own. */
const ALL = "all";
/** How often an open list of requests looks for new ones. */
const POLL_MS = 10_000;
/** Longest decision reason the API takes. */
const MAX_REASON = 200;

/** The status filter lives in the URL; a supervisor lands on what is waiting for them. */
type StatusSearch = { status?: DiscountApprovalStatus | typeof ALL };

function validateSearch(search: Record<string, unknown>): StatusSearch {
  const status = search.status;
  if (status === ALL) return { status: ALL };
  return DISCOUNT_APPROVAL_STATUSES.includes(status as DiscountApprovalStatus)
    ? { status: status as DiscountApprovalStatus }
    : {};
}

export const Route = createFileRoute("/_authed/discount-approvals")({
  staticData: { section: "workspace", crumb: "nav.discountApprovals" },
  validateSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: DiscountApprovalsPage,
});

type T = AreaTranslate<typeof discounts.id>;

const STATUS_KEYS = {
  pending: "discounts.approvals.status.pending",
  approved: "discounts.approvals.status.approved",
  rejected: "discounts.approvals.status.rejected",
  expired: "discounts.approvals.status.expired",
  consumed: "discounts.approvals.status.consumed",
} as const satisfies Record<DiscountApprovalStatus, string>;

const STATUS_VARIANTS = {
  pending: "warning",
  approved: "success",
  rejected: "destructive",
  expired: "outline",
  consumed: "secondary",
} as const satisfies Record<DiscountApprovalStatus, string>;

function StatusBadge(props: { status: DiscountApprovalStatus }) {
  const { t } = useMessages(discounts);
  return <Badge variant={STATUS_VARIANTS[props.status]}>{t(STATUS_KEYS[props.status])}</Badge>;
}

/** Share of the gross subtotal, exact to two decimals: what the outlet threshold compares. */
const shareBasisPoints = (discount: string, subtotal: string) =>
  BigInt(subtotal) === 0n ? 0 : Number((BigInt(discount) * 10_000n) / BigInt(subtotal));

function approvalColumns(t: T, timezone: string | undefined): DataTableColumn<DiscountApprovalSummary>[] {
  return [
    {
      id: "requested",
      header: t("discounts.approvals.requested"),
      meta: { card: "title" },
      cell: ({ row }) => <Requested approval={row.original} timezone={timezone} />,
    },
    {
      id: "requester",
      header: t("discounts.approvals.requester"),
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate">{row.original.requestedBy}</span>
          <span className="line-clamp-2 text-muted-foreground text-sm">{row.original.reason}</span>
        </div>
      ),
    },
    {
      id: "discount",
      header: t("discounts.approvals.discount"),
      meta: { align: "end" },
      cell: ({ row }) => <DiscountAmount approval={row.original} />,
    },
    {
      id: "status",
      header: t("common.status"),
      meta: { className: "md:w-36" },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
  ];
}

function Requested(props: { approval: DiscountApprovalSummary; timezone: string | undefined }) {
  const { format } = useMessages(discounts);
  return (
    <div className="flex min-w-0 flex-col">
      <time className="font-medium" dateTime={new Date(props.approval.requestedAt).toISOString()}>
        {format.dateTime(props.approval.requestedAt, props.timezone)}
      </time>
      <span className="truncate text-muted-foreground text-sm">{props.approval.registerName}</span>
    </div>
  );
}

function DiscountAmount(props: { approval: DiscountApprovalSummary }) {
  const { t, format } = useMessages(discounts);
  return (
    <div className="flex flex-col items-end">
      <span className="tabular-nums">−{format.money(BigInt(props.approval.discountTotal))}</span>
      <span className="text-muted-foreground text-sm tabular-nums">
        {t("discounts.approvals.ofSubtotal", { subtotal: format.money(BigInt(props.approval.subtotal)) })}
      </span>
    </div>
  );
}

/**
 * Supervisor approvals (R1-17): requests from cashiers whose discounts exceed the outlet's threshold.
 * A different owner or outlet manager decides from their own session, with a reason; the decision is
 * audited and an approval covers the exact quoted cart for 15 minutes.
 */
function DiscountApprovalsPage() {
  const { t } = useMessages(discounts);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const reviewable = outlets.filter((o) => canAt(scopes, "discounts.override", o.id));
  const [outletId, setOutletId] = useState(
    reviewable.find((o) => o.id === current?.id)?.id ?? reviewable[0]?.id ?? "",
  );
  const outlet = reviewable.find((o) => o.id === outletId);
  const search = Route.useSearch();
  const status = search.status === ALL ? undefined : (search.status ?? "pending");
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useInfiniteQuery({
    ...discountApprovalsQuery(outletId, status),
    refetchInterval: POLL_MS,
  });
  const columns = useMemo(() => approvalColumns(t, outlet?.timezone), [t, outlet?.timezone]);
  const [reviewing, setReviewing] = useState<string | null>(null);

  if (reviewable.length === 0) {
    return (
      <Page>
        <PageHeader title={t("nav.discountApprovals")} subtitle={t("discounts.approvals.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title={t("nav.discountApprovals")} subtitle={t("discounts.approvals.subtitle")} />
      <DataTable
        paging="pages"
        label={t("discounts.approvals.list")}
        columns={columns}
        query={list}
        getRowId={(approval) => approval.id}
        filters={
          <>
            {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
            <OptionSelect
              aria-label={t("outlet.heading")}
              className="md:w-56"
              options={reviewable.map((o) => ({ value: o.id, label: o.name }))}
              value={outletId}
              onValueChange={setOutletId}
            />
            <OptionSelect
              aria-label={t("common.status")}
              className="md:w-56"
              options={[
                { value: ALL, label: t("discounts.approvals.status.all") },
                ...DISCOUNT_APPROVAL_STATUSES.map((s) => ({ value: s, label: t(STATUS_KEYS[s]) })),
              ]}
              value={status ?? ALL}
              onValueChange={(value) =>
                void navigate({
                  search: value === "pending" ? {} : { status: value as NonNullable<StatusSearch["status"]> },
                  replace: true,
                })
              }
            />
          </>
        }
        rowActions={(approval) => [
          {
            label: t(
              approval.status === "pending" ? "discounts.approvals.review" : "discounts.approvals.view",
            ),
            icon: approval.status === "pending" ? <ScaleIcon /> : <EyeIcon />,
            onSelect: () => setReviewing(approval.id),
          },
        ]}
        empty={{
          title: t("discounts.approvals.emptyTitle"),
          description: t("discounts.approvals.emptyHint"),
        }}
      />
      {reviewing && outlet && (
        <ReviewDialog approvalId={reviewing} outlet={outlet} onClose={() => setReviewing(null)} />
      )}
    </Page>
  );
}

function reviewError(t: T, error: unknown): string {
  if (!(error instanceof ApiRequestError)) return t("common.saveOffline");
  switch (error.code) {
    case "discount_self_approval":
      return t("discounts.approvals.selfApproval");
    case "discount_approval_not_pending":
      return t("discounts.approvals.notPending");
    case "discount_approval_revision_conflict":
      return t("discounts.approvals.revisionConflict");
    case "discount_quote_stale":
      return t("discounts.approvals.stale");
    case "discount_not_eligible":
    case "discount_selection_invalid":
    case "not_for_sale":
    case "invalid_modifiers":
      return t("discounts.approvals.notEligible");
    case "discount_state_busy":
      return t("discounts.approvals.busy");
    default:
      return t("common.saveFailed");
  }
}

/** The exact quoted cart a cashier asked about, and the decision with its reason. Mounted while open. */
function ReviewDialog(props: { approvalId: string; outlet: OutletSummary; onClose: () => void }) {
  const { t, format } = useMessages(discounts);
  const queryClient = useQueryClient();
  const detail = useQuery(discountApprovalQuery(props.approvalId));
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const approval = detail.data;
  const decide = useMutation({
    mutationFn: async (decision: "approve" | "reject") => {
      if (!approval) throw new Error("not loaded");
      return unwrap(
        await api.v1["discount-approvals"]({ approvalId: approval.id }).review.post({
          decision,
          expectedRevision: approval.revision,
          reason: reason.trim(),
        }),
      );
    },
    onSuccess: async (reviewed, decision) => {
      queryClient.setQueryData(discountApprovalQuery(reviewed.id).queryKey, reviewed);
      await queryClient.invalidateQueries({ queryKey: [...discountApprovalsKey, "list"] });
      toast.success(
        t(decision === "approve" ? "discounts.approvals.approved" : "discounts.approvals.rejectedToast", {
          name: reviewed.requestedBy,
        }),
      );
      props.onClose();
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && error.code === "discount_approval_revision_conflict")
        void detail.refetch();
    },
  });
  const pending = approval?.status === "pending";
  const ready = pending && reason.trim().length > 0 && !decide.isPending;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (ready) decide.mutate("approve");
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={
        approval ? t("discounts.approvals.reviewTitle", { name: approval.requestedBy }) : t("kit.loading")
      }
      {...(approval
        ? {
            description: t("discounts.approvals.reviewHint", {
              register: approval.registerName,
              time: format.dateTime(approval.requestedAt, props.outlet.timezone),
            }),
          }
        : {})}
      dismissible={!decide.isPending}
      className="md:max-w-xl"
      footer={
        pending ? (
          <>
            <Button
              type="button"
              variant="destructive"
              disabled={!ready}
              onClick={() => decide.mutate("reject")}
            >
              {t("discounts.approvals.reject")}
            </Button>
            <Button type="submit" form="discount-review" disabled={!ready}>
              {t(decide.isPending ? "discounts.approvals.deciding" : "discounts.approvals.approve")}
            </Button>
          </>
        ) : undefined
      }
    >
      {detail.isError && (
        <div role="alert" className="flex flex-col gap-2 text-sm">
          <p className="text-destructive">{t("discounts.approvals.loadFailed")}</p>
          <Button type="button" variant="outline" onClick={() => void detail.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      )}
      {detail.isPending && <p role="status">{t("kit.loading")}</p>}
      {approval && (
        <form id="discount-review" className="flex flex-col gap-4" onSubmit={submit} noValidate>
          <ApprovalDetail approval={approval} timezone={props.outlet.timezone} />
          {pending && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={reasonId}>{t("discounts.approvals.decision")}</Label>
              <Textarea
                id={reasonId}
                value={reason}
                maxLength={MAX_REASON}
                placeholder={t("discounts.approvals.decisionPlaceholder")}
                disabled={decide.isPending}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          )}
          {decide.isError && (
            <p role="alert" className="text-destructive text-sm">
              {reviewError(t, decide.error)}
            </p>
          )}
        </form>
      )}
    </ResponsiveDialog>
  );
}

function ApprovalDetail(props: { approval: DiscountApprovalView; timezone: string }) {
  const { approval, timezone } = props;
  const { t, format, locale } = useMessages(discounts);
  const quote = approval.quote;
  const money = (amount: string) => format.money(BigInt(amount));
  const limit = percentText(quote.policy.cashierThresholdBasisPoints, locale);
  const share = percentText(shareBasisPoints(approval.discountTotal, approval.subtotal), locale);
  return (
    <div className="flex flex-col gap-4 text-sm">
      <div className="flex items-center justify-between gap-2">
        <StatusBadge status={approval.status} />
        <span className="text-muted-foreground">{t("discounts.approvals.share", { share, limit })}</span>
      </div>
      <div className="flex flex-col gap-1 rounded-lg border p-3">
        <span className="font-medium">{t("discounts.approvals.reason")}</span>
        <p className="whitespace-pre-wrap">{approval.reason}</p>
      </div>
      {/* Not virtualised (CODING_STANDARDS §4 exception): an order has at most MAX_ORDER_LINES lines. */}
      <ul className="flex flex-col divide-y" aria-label={t("discounts.approvals.lines")}>
        {quote.lines.map((line) => (
          <li key={line.position} className="flex flex-col gap-0.5 py-2">
            <div className="flex justify-between gap-3">
              <span className="min-w-0">
                {line.quantity} × {line.name}
                {line.variantName && line.variantName !== line.name ? ` · ${line.variantName}` : ""}
              </span>
              <span className="tabular-nums">{money(line.lineTotal)}</span>
            </div>
            {line.lineDiscount && BigInt(line.lineDiscountAmount) > 0n && (
              <div className="flex justify-between gap-3 text-muted-foreground">
                <span>
                  {t("discounts.approvals.lineDiscount", { name: line.lineDiscount.name })} (
                  {discountValueText(line.lineDiscount.value, format, locale)})
                </span>
                <span className="tabular-nums">−{money(line.lineDiscountAmount)}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
      <dl className="flex flex-col gap-1 rounded-lg bg-muted p-3">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{t("discounts.approvals.subtotal")}</dt>
          <dd className="tabular-nums">{money(quote.subtotal)}</dd>
        </div>
        {quote.orderDiscount && BigInt(quote.orderDiscountAmount) > 0n && (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">
              {t("discounts.approvals.orderDiscount", { name: quote.orderDiscount.name })} (
              {discountValueText(quote.orderDiscount.value, format, locale)})
            </dt>
            <dd className="tabular-nums">−{money(quote.orderDiscountAmount)}</dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{t("discounts.approvals.discountTotal")}</dt>
          <dd className="tabular-nums">−{money(quote.discountTotal)}</dd>
        </div>
        <div className="flex justify-between font-semibold text-base">
          <dt>{t("discounts.approvals.total")}</dt>
          <dd className="tabular-nums">{money(quote.total)}</dd>
        </div>
      </dl>
      {approval.reviewedBy && approval.reviewedAt && (
        <div className="flex flex-col gap-0.5 text-muted-foreground">
          <p>
            {t("discounts.approvals.reviewedBy", {
              name: approval.reviewedBy,
              time: format.dateTime(approval.reviewedAt, timezone),
            })}
          </p>
          {approval.reviewReason && (
            <p>{t("discounts.approvals.reviewReason", { reason: approval.reviewReason })}</p>
          )}
          {approval.expiresAt && approval.status === "approved" && (
            <p>
              {t("discounts.approvals.expiresAt", { time: format.dateTime(approval.expiresAt, timezone) })}
            </p>
          )}
          {approval.consumedAt && (
            <p>
              {t("discounts.approvals.consumedAt", { time: format.dateTime(approval.consumedAt, timezone) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
